// The presentation schema — one authority, three readers.
//
// A deck is a typed list of slides, never free-form HTML. The model chooses a
// layout and fills its named fields; the layout owns the geometry. That is what
// makes the same deck render identically in the canvas (ui/workspace/deck/) and
// in the exported .pptx (../decks/pptx.mjs), and what keeps generated output
// inside the design system instead of inventing a page.
//
// The model never reads a prose catalogue of these layouts: `deckPayload` is
// projected to JSON Schema as the `save_work_result` payload variant, so the
// field names it is given ARE this file. Adding a layout means adding it here,
// in the canvas renderer and in the exporter — there is deliberately no
// fallback that makes a missing one look acceptable.
//
// REJECT and REVIEW are different failures and are kept apart. A nine-bullet
// slide cannot render, so the schema rejects it and the model repairs from the
// error. A ninety-character title renders badly but renders, so reviewDeck()
// returns it as a note on the tool receipt. Rejecting the second would spend a
// round trip enforcing taste.
import { z } from 'zod';
import { ImageFit, ImageRatio, ManagedImageSlot } from '../../artifacts/images/values.mjs';

// Slide capacities are geometry, not preference: each is the count past which
// the layout's own box runs out of room in the 1280x720 space.
export const DECK_LIMITS = Object.freeze({
  slides: 40,
  bullets: 6,
  agendaItems: 6,
  columnBullets: 5,
  stats: 4,
  kpis: 6,
  steps: 5,
  tableRows: 12,
  tableColumns: 5,
  series: 4,
  categories: 12,
  points: 4,
  asks: 4,
  images: 4,
});

// Mirrors the text() helper in contract.mjs: trimmed, non-empty, and free of
// C0 controls, which are never legitimate in a rendered line.
const CONTROL_CHARACTER = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/;
const line = (max) =>
  z
    .string()
    .trim()
    .min(1)
    .max(max)
    .refine((s) => !CONTROL_CHARACTER.test(s));
const bounded = (schema, min, max) => z.array(schema).min(min).max(max);

const TITLE = line(90);
const LEAD = line(200);
const EYEBROW = line(40);
const LABEL = line(60);
const DETAIL = line(120);
const VALUE = line(14);

// Speaker notes travel with the slide and land in the .pptx notes pane. They
// are never drawn on the slide, so they hold the detail the slide leaves out.
const common = { notes: line(600).optional() };

const takeaway = line(160).optional();
const source = line(120).optional();

const retainedSlideSchemas = {
  cover: {
    summary: 'Opening slide: deck title on a dark field.',
    shape: {
      eyebrow: EYEBROW.optional(),
      title: TITLE,
      subtitle: LEAD.optional(),
      footnote: line(90).optional(),
    },
  },
  agenda: {
    summary: 'What the deck covers, as a numbered list.',
    shape: {
      title: TITLE,
      items: bounded(
        z.object({ label: LABEL, detail: DETAIL.optional() }).strict(),
        2,
        DECK_LIMITS.agendaItems,
      ),
    },
  },
  section: {
    summary: 'Divider announcing the next part. Carries no data.',
    shape: { eyebrow: EYEBROW.optional(), title: TITLE, support: LEAD.optional() },
  },
  statement: {
    summary: 'One sentence at display size. For the single claim of the deck.',
    shape: { statement: line(140), support: LEAD.optional() },
  },
  bullets: {
    summary: 'The default content slide: a claim and the points supporting it.',
    shape: {
      title: TITLE,
      lead: LEAD.optional(),
      bullets: bounded(
        z.object({ text: LABEL, detail: DETAIL.optional() }).strict(),
        1,
        DECK_LIMITS.bullets,
      ),
      takeaway,
    },
  },
  'two-column': {
    summary: 'Two independent halves, each either prose or its own bullets.',
    shape: {
      title: TITLE,
      lead: LEAD.optional(),
      columns: bounded(
        z
          .object({
            heading: LABEL,
            body: line(360).optional(),
            bullets: bounded(LABEL, 1, DECK_LIMITS.columnBullets).optional(),
          })
          .strict()
          // A column draws body OR bullets. Both would occupy the same box.
          .refine((column) => Boolean(column.body) !== Boolean(column.bullets), {
            message: 'Give a column either body or bullets, not both and not neither',
          }),
        2,
        2,
      ),
      takeaway,
    },
  },
  'stat-trio': {
    summary: 'Two to four headline numbers as cards, for the figures carrying the argument.',
    shape: {
      title: TITLE,
      lead: LEAD.optional(),
      stats: bounded(
        z
          .object({
            value: VALUE,
            label: LABEL,
            // Change against a stated baseline, e.g. "+12% YoY".
            delta: line(20).optional(),
            note: DETAIL.optional(),
          })
          .strict(),
        2,
        DECK_LIMITS.stats,
      ),
      takeaway,
    },
  },
  'kpi-grid': {
    summary: 'Three to six measures in a grid. A scorecard, not an argument.',
    shape: {
      title: TITLE,
      kpis: bounded(
        z.object({ value: VALUE, label: LABEL, note: DETAIL.optional() }).strict(),
        3,
        DECK_LIMITS.kpis,
      ),
      takeaway,
    },
  },
  chart: {
    summary:
      'One chart with its own title. column for magnitude, bar when labels are long, line for change over time, stacked-column for composition over time, stacked-bar for parts of a whole. Values are plotted, never described in prose.',
    shape: {
      title: TITLE,
      lead: LEAD.optional(),
      chart: z
        .object({
          type: z.enum(['column', 'bar', 'line', 'stacked-column', 'stacked-bar']),
          categories: bounded(line(40), 1, DECK_LIMITS.categories),
          series: bounded(
            z
              .object({
                name: LABEL,
                // null is "not measured": it leaves a gap and is never drawn as zero.
                values: bounded(z.number().finite().nullable(), 1, DECK_LIMITS.categories),
              })
              .strict(),
            1,
            DECK_LIMITS.series,
          ),
          // Axis and label formatting only; the values stay raw numbers.
          valueFormat: z.enum(['number', 'currency', 'percent']).default('number'),
          unit: line(12).optional(),
        })
        .strict(),
      source,
      takeaway,
    },
  },
  table: {
    summary: 'A small grid, for exact values a chart would blur.',
    shape: {
      title: TITLE,
      lead: LEAD.optional(),
      columns: bounded(
        z.object({ label: LABEL, align: z.enum(['left', 'right']).default('left') }).strict(),
        2,
        DECK_LIMITS.tableColumns,
      ),
      rows: bounded(bounded(line(60), 2, DECK_LIMITS.tableColumns), 1, DECK_LIMITS.tableRows),
      source,
      takeaway,
    },
  },
  comparison: {
    summary: 'Two sides weighed against each other, point for point.',
    shape: {
      title: TITLE,
      lead: LEAD.optional(),
      left: z.object({ heading: LABEL, points: bounded(LABEL, 1, DECK_LIMITS.points) }).strict(),
      right: z.object({ heading: LABEL, points: bounded(LABEL, 1, DECK_LIMITS.points) }).strict(),
      takeaway,
    },
  },
  timeline: {
    summary: 'Two to five ordered steps along a track, for a plan or a sequence.',
    shape: {
      title: TITLE,
      lead: LEAD.optional(),
      steps: bounded(
        z.object({ label: LABEL, detail: DETAIL.optional(), marker: line(12).optional() }).strict(),
        2,
        DECK_LIMITS.steps,
      ),
      takeaway,
    },
  },
  quote: {
    summary: 'A verbatim quotation. Never paraphrase into this layout.',
    shape: { quote: line(240), attribution: LABEL.optional(), role: DETAIL.optional() },
  },
  closing: {
    summary: 'Final slide: the decision asked for, or the asks.',
    shape: {
      title: TITLE,
      support: LEAD.optional(),
      asks: bounded(LABEL, 0, DECK_LIMITS.asks).default([]),
      footnote: line(90).optional(),
    },
  },
};

// Managed images belong to the same named-layout authority. Their immutable
// slots contain no URL; current pixels are supplied only by the authorized
// media projection. Retained Result readers keep their original fourteen.
export const deckImageSchema = z
  .object({
    media: ManagedImageSlot,
    alt: z
      .string()
      .max(1000)
      .refine((s) => !CONTROL_CHARACTER.test(s)),
    ratio: ImageRatio,
    fit: ImageFit,
    cropSafe: z.boolean(),
  })
  .strict()
  .refine((image) => image.fit !== 'cover' || image.cropSafe, {
    message: 'image_cover_requires_crop_safe',
  });
export const deckImageItemSchema = z
  .object({ image: deckImageSchema, name: LABEL, value: VALUE.optional() })
  .strict();
const imageSlideSchemas = {
  'image-feature': {
    shape: {
      title: TITLE,
      image: deckImageSchema,
      caption: line(200).optional(),
      takeaway,
      source,
    },
  },
  'image-text': {
    shape: {
      title: TITLE,
      image: deckImageSchema,
      text: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('body'), body: line(360) }).strict(),
        z
          .object({
            kind: z.literal('bullets'),
            bullets: bounded(
              z.object({ text: LABEL, detail: DETAIL.optional() }).strict(),
              1,
              DECK_LIMITS.bullets,
            ),
          })
          .strict(),
      ]),
      source,
    },
  },
  'image-grid': {
    shape: { title: TITLE, items: bounded(deckImageItemSchema, 2, DECK_LIMITS.images), source },
  },
};
const slideSchemas = { ...retainedSlideSchemas, ...imageSlideSchemas };
export const RETAINED_DECK_LAYOUTS = Object.freeze(Object.keys(retainedSlideSchemas));
export const DECK_IMAGE_LAYOUTS = Object.freeze(Object.keys(imageSlideSchemas));
export const DECK_LAYOUTS = Object.freeze(Object.keys(slideSchemas));

const slidesFor = (layouts) =>
  z.discriminatedUnion(
    'layout',
    layouts.map((layout) =>
      z.object({ layout: z.literal(layout), ...common, ...slideSchemas[layout].shape }).strict(),
    ),
  );
export const deckSlideSchema = slidesFor(DECK_LAYOUTS);
export const deckImageSlideSchema = slidesFor(DECK_IMAGE_LAYOUTS);
export const retainedDeckSlideSchema = slidesFor(RETAINED_DECK_LAYOUTS);

export const deckPayload = z
  .object({
    title: TITLE,
    subtitle: LEAD.optional(),
    slides: bounded(retainedDeckSlideSchema, 1, DECK_LIMITS.slides),
  })
  .strict()
  .describe(
    'For kind "deck": { "title", "slides": [{ "layout", ...that layout\'s own fields }] }. Each layout accepts only its own named fields; no other key is accepted and there is no free-form HTML.',
  );
